/* eslint-disable react/prop-types */
import React, {
  Dispatch,
  forwardRef,
  SetStateAction,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { COREHeading } from "../Typography/COREHeading";
import { ChartDataType, COREChart, COREChartProps } from "./COREChart";
import {
  canvasLegendBlock,
  canvasTitleBlock,
  drawLegendBlock,
  drawPlotlyBlock,
  drawTitleChartBlock,
  setPlotlyImage,
} from "./ChartUtils/imageUtils";
import { downloadFileName } from "./ChartUtils/csvUtils";
import { CORELegend } from "./CORELegend";
import "./COREChart.less";
import { AggregatesChartProps } from "../../modules/chartBuilder/AggregatesChartV2";
import { CORETagProps } from "../Content/CORETag";
import { generateTestId, TestID } from "../../shared/testids/testids";
import ImgCOREMarketsLogoSVG from "../../assets/img/COREMarketsLogoForDownload.svg";
import ImgCOREMarketsLogoForDarkThemeSVG from "../../assets/img/COREMarketsLogoForDownloadDarkTheme.svg";
import { Col, Row } from "antd";
import { COREDivider } from "../Layout/COREDivider";
import { PlotRefType } from "./ChartHOC";
import equal from "fast-deep-equal";
import { spacing } from "../../shared/global";
import classNames from "classnames";

export type Legend = Pick<
  CORETagProps,
  "label" | "color" | "checked" | "tagStyle"
> & {
  key: string;
};

type COREChartLegendProps = Pick<
  AggregatesChartProps,
  | "chartId"
  | "chartTitle"
  | "downloadCsv"
  | "downloadPng"
  | "downloadPngCanvas"
  | "enableEmptyIcon"
  | "testID"
  | "showlegend"
  | "legendFilter"
  | "plotRef"
  | "chartPanel"
  | "ref"
  | "empty"
> &
  Pick<
    COREChartProps,
    | "data"
    | "layout"
    | "configRangeOfYAxis"
    | "hoverColor"
    | "isWidget"
    | "fitParent"
    | "customLayout"
    | "onPlotlyHover"
    | "onPlotlyUnhover"
    | "onPlotlyClick"
    | "customTooltip"
    | "interactiveTooltip"
  > & {
    legends: Legend[];
    onLegendsChange?: (legends: Legend[]) => void;
    /**
     * Whether the exported PNG carries the legend block. Defaults to
     * `showlegend`, except for a chart with controlled legends, which defaults
     * to always exporting it — see `callDrawImage`.
     *
     * Set this when a chart hides the legend on screen for only some of its
     * views and wants the export to follow.
     */
    showlegendInExport?: boolean;
    screenShotSize?: {
      width: number;
      height: number;
    };
    allowEmptyChart?: boolean;
  };

/**
 * The pieces a chart contributes to a PNG: the plot itself, its title block and
 * its legend block. `drawImageParts` on the chart's ref hands these back instead
 * of downloading them, for a caller composing several charts into one image.
 */
export type ChartImageParts = {
  plot?: HTMLImageElement;
  title?: HTMLCanvasElement;
  legend?: HTMLCanvasElement;
};

export const drawImageIntoCanvas = async (
  plotRef: COREChartProps["plotRef"],
  titleRef: COREChartProps["titleRef"],
  legendRef: COREChartProps["legendRef"],
  chartTitle: COREChartProps["chartTitle"],
  showlegend: COREChartLegendProps["showlegend"],
  screenShotSize?: COREChartLegendProps["screenShotSize"]
) => {
  const canvasTotalWidth = screenShotSize ? screenShotSize.width : 1280;
  const canvasTotalHeight = screenShotSize ? screenShotSize.height : 780;
  const basePadding = 24;
  const spaceBetween = 16;

  if (!plotRef || !titleRef || !legendRef) {
    console.error("One or more refs are null or undefined.");
    return;
  }

  const plotlyBlock = await setPlotlyImage(plotRef);
  const titleBlock = await canvasTitleBlock({
    titleDOM: titleRef,
  });
  const legendBlock = await canvasLegendBlock({
    legendDOM: legendRef,
  });
  const chartBgColor =
    getComputedStyle(document.documentElement).getPropertyValue(
      "--core-chart-bgcolor"
    ) || "white";

  const COREMarkets = new Image();
  COREMarkets.src =
    chartBgColor === "#05060A"
      ? ImgCOREMarketsLogoForDarkThemeSVG
      : ImgCOREMarketsLogoSVG;
  COREMarkets.alt = "COREMarkets-logo";

  const titleBlockHeight = titleBlock?.height
    ? titleBlock?.height + spaceBetween
    : 0;
  const legendBlockHeight = legendBlock?.height
    ? legendBlock?.height + spaceBetween
    : 0;

  const finalCanvas = document.createElement("canvas");
  finalCanvas.setAttribute("width", `${canvasTotalWidth}`);
  finalCanvas.setAttribute("height", `${canvasTotalHeight}`);

  const ctx = finalCanvas.getContext("2d");
  if (ctx === null) throw Error("error");

  ctx.fillStyle = chartBgColor.trim();
  ctx.fillRect(0, 0, canvasTotalWidth, canvasTotalHeight);

  COREMarkets.onload = () => {
    drawTitleChartBlock({
      titleBlock: titleBlock,
      ctx: ctx,
      canvasTotalWidth: canvasTotalWidth,
      titleMarginTop: basePadding,
    });

    if (showlegend) {
      drawLegendBlock({
        legendBlock: legendBlock,
        ctx: ctx,
        titleBlockHeight: titleBlockHeight,
        marginLegendTop: spaceBetween,
        canvasTotalWidth: canvasTotalWidth,
      });
    }
    drawPlotlyBlock({
      plotlyBlock: plotlyBlock,
      ctx: ctx,
      canvasTotalWidth: canvasTotalWidth,
      titleBlockHeight: titleBlockHeight,
      legendBlockHeight: legendBlockHeight,
      canvasTotalHeight: canvasTotalHeight,
    });

    ctx.drawImage(
      COREMarkets,
      0,
      0,
      COREMarkets.width,
      COREMarkets.height,
      canvasTotalWidth - COREMarkets.width - basePadding,
      canvasTotalHeight - (COREMarkets.height + 16),
      COREMarkets.width,
      COREMarkets.height
    );

    const image = finalCanvas.toDataURL("image/png", 1.0);
    const tagA = document.createElement("a");
    tagA.setAttribute(
      "download",
      downloadFileName(
        chartTitle ? `${chartTitle.replaceAll("$", "")}` : "Newplot"
      )
    );
    tagA.setAttribute("href", image);
    tagA.click();
  };
};

const checkDataExist = (d?: Partial<ChartDataType>) => {
  if (!!d && d.type === "bar") {
    const y = d.mode !== "markers" ? (d.y as number[]) ?? [] : [];
    return y.length > 0 && y.filter((value) => value).length > 0;
  }
  return true;
};

const ChartLegend: React.FC<COREChartLegendProps> = forwardRef(
  ({ data, legends, ...props }, ref) => {
    const filteredLegends = legends.filter((l) =>
      checkDataExist(data.find((d) => l.label === d.name))
    );
    return (
      <ChartLegendBody
        ref={ref}
        data={data}
        legends={filteredLegends}
        {...props}
      />
    );
  }
);

const emptyDataCheck = (data: Partial<ChartDataType>[]) => {
  if (data.length === 0) return true;
  return data.every((datalist) => {
    return (
      (datalist.x?.length === 0 && datalist.y?.length === 0) ||
      (!datalist.y?.some((v) => v !== null) &&
        !datalist.r?.some((v) => v !== null))
    );
  });
};

const ChartLegendBodyPNG: React.FC<
  Pick<COREChartLegendProps, "layout" | "testID" | "legendFilter"> & {
    isTitleHidden: boolean;
    selectLegend: Legend[];
    setSelectLegend: Dispatch<SetStateAction<Legend[]>>;
    titleRef: React.RefObject<HTMLDivElement>;
    legendRef: React.RefObject<HTMLDivElement>;
  }
> = ({
  layout,
  testID,
  isTitleHidden,
  legendFilter,
  selectLegend,
  setSelectLegend,
  titleRef,
  legendRef,
}) => {
  const ScreenSizeWidth = 1200;

  return (
    <Row
      className={"chart-title-by-file-name-block"}
      hidden={isTitleHidden}
      align={"middle"}
      justify={"center"}
      gutter={[spacing.xxs, spacing.xxs]}
    >
      <Col ref={titleRef} className={"chart-title-block"}>
        <COREHeading
          level={3}
          marginBottom={true}
          testID={generateTestId(
            "chartbuilder",
            "show-title-of-chart-by-file-name"
          )}
        >
          {layout.filename}
        </COREHeading>
      </Col>
      <Col span={24}>
        <COREDivider space="none"></COREDivider>
      </Col>
      <div style={{ width: `${ScreenSizeWidth}px` }} ref={legendRef}>
        <CORELegend
          selectLegend={selectLegend}
          setSelectLegend={setSelectLegend}
          legendFilter={legendFilter}
          testID={`${testID}-legend` as TestID}
          maxWidth={172}
          isShowModal={false}
        />
      </div>
    </Row>
  );
};

const ChartLegendBody: React.FC<COREChartLegendProps> = forwardRef(
  (
    {
      chartId,
      chartTitle,
      downloadCsv,
      downloadPng,
      downloadPngCanvas,
      data,
      layout,
      configRangeOfYAxis,
      hoverColor,
      enableEmptyIcon,
      testID,
      legends,
      showlegend,
      legendFilter,
      plotRef,
      chartPanel,
      empty,
      screenShotSize = {
        width: 1280,
        height: 780,
      },
      isWidget,
      fitParent: fitParentProp,
      customLayout,
      onPlotlyHover,
      onPlotlyUnhover,
      onPlotlyClick,
      customTooltip,
      interactiveTooltip,
      onLegendsChange,
      showlegendInExport,
      allowEmptyChart,
    },
    ref
  ) => {
    const isControlled = onLegendsChange !== undefined;
    const [internalLegends, setInternalLegends] = useState(legends);
    const activeLegends = isControlled ? legends : internalLegends;

    const handleLegendChange: Dispatch<SetStateAction<Legend[]>> = (value) => {
      const newLegends =
        typeof value === "function" ? value(activeLegends) : value;

      if (isControlled) {
        onLegendsChange(newLegends);
      } else {
        setInternalLegends(newLegends);
      }
    };

    // Widgets were the first container to need the legend and chart to share a
    // flex column that fills the parent; full screen is the second.
    const fitParent = fitParentProp || isWidget;

    const legendRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLDivElement>(null);
    const defaultPlotRef = useRef<PlotRefType>();
    const [isTitleHidden, setIsTitleHidden] = useState<boolean>(true);

    // Sync props to state in uncontrolled mode
    useEffect(() => {
      if (!isControlled) {
        setInternalLegends(legends);
      }
    }, [legends, isControlled]);

    useImperativeHandle(ref, () => ({
      callDrawImage() {
        setIsTitleHidden(false);
        drawImageIntoCanvas(
          plotRef ?? defaultPlotRef,
          titleRef,
          legendRef,
          chartTitle,
          // A controlled chart defaults to exporting the legend whatever
          // `showlegend` says: it may be hiding the on-screen panel only
          // because the legend is rendered elsewhere, and the export still
          // wants it. `showlegendInExport` is the way to opt out of that.
          showlegendInExport ?? (isControlled ? true : showlegend),
          screenShotSize
        ).then(() => {
          setIsTitleHidden(true);
        });
      },
      // Captures the same blocks callDrawImage() draws, but returns them so a
      // caller stacking several charts can compose one image out of them. The
      // title and legend blocks only exist while shown, so they are unhidden for
      // the capture and hidden again afterwards.
      async drawImageParts(): Promise<ChartImageParts> {
        setIsTitleHidden(false);
        try {
          const plot = await setPlotlyImage(plotRef ?? defaultPlotRef);
          // The blocks are painted by the render setIsTitleHidden triggered; wait
          // for it before reading their size, or they measure as zero.
          await new Promise((resolve) => requestAnimationFrame(resolve));
          const title = await canvasTitleBlock({ titleDOM: titleRef });
          const legend =
            (isControlled ? true : showlegend) !== false
              ? await canvasLegendBlock({ legendDOM: legendRef })
              : undefined;
          return { plot, title, legend };
        } finally {
          setIsTitleHidden(true);
        }
      },
    }));

    const chartData: Partial<ChartDataType>[] = data.filter(
      (d) =>
        activeLegends.map((l) => l.label).includes(d.name) &&
        activeLegends.find((l) => l.label === d.name)?.checked
    );

    return (
      <Row
        className={classNames([{ "core-chart-legend-fit-parent": fitParent }])}
        align={"middle"}
        gutter={fitParent ? [0, 0] : [0, spacing.md]}
      >
        {showlegend && activeLegends.length !== 0 && (
          <Col
            className={classNames([
              { "core-chart-legend-layout-core-legend": fitParent },
            ])}
            span={24}
          >
            <CORELegend
              selectLegend={activeLegends}
              setSelectLegend={handleLegendChange}
              legendFilter={legendFilter}
              testID={`${testID}-main-legend` as TestID}
              maxWidth={172}
            />
          </Col>
        )}
        <Col
          className={classNames([
            { "core-chart-legend-layout-core-chart": fitParent },
          ])}
          span={24}
        >
          {!emptyDataCheck(chartData) || allowEmptyChart ? (
            <COREChart
              chartId={chartId}
              chartTitle={chartTitle}
              testID={testID}
              plotRef={plotRef ?? defaultPlotRef}
              legendRef={legendRef}
              titleRef={titleRef}
              downloadCsv={downloadCsv}
              downloadPng={downloadPng}
              downloadPngCanvas={downloadPngCanvas}
              data={chartData}
              layout={layout}
              useResizeHandler={true}
              configRangeOfYAxis={configRangeOfYAxis}
              config={{
                displaylogo: false,
                displayModeBar: false,
              }}
              showTimeButtons={false}
              hoverColor={hoverColor}
              enableEmptyIcon={enableEmptyIcon}
              fitParent={fitParent}
              customLayout={customLayout}
              onPlotlyHover={onPlotlyHover}
              onPlotlyUnhover={onPlotlyUnhover}
              onPlotlyClick={onPlotlyClick}
              customTooltip={customTooltip}
              interactiveTooltip={interactiveTooltip}
            />
          ) : (
            empty
          )}
        </Col>
        {chartPanel && layout.filename && (
          <ChartLegendBodyPNG
            testID={testID}
            layout={layout}
            isTitleHidden={isTitleHidden}
            selectLegend={activeLegends}
            setSelectLegend={handleLegendChange}
            titleRef={titleRef}
            legendRef={legendRef}
          />
        )}
      </Row>
    );
  }
);

export const COREChartLegend = React.memo<
  React.ComponentType<COREChartLegendProps & { ref?: React.ForwardedRef<any> }>
>(
  ChartLegend,
  (prevProps, nextProps) =>
    prevProps &&
    equal(prevProps.data, nextProps.data) &&
    equal(prevProps.layout, nextProps.layout) &&
    equal(prevProps.legends, nextProps.legends)
);
